<template>
  <div class="container">
    <van-nav-bar :title="$t('recharge.recharge_record')">
      <template #left>
        <van-icon name="arrow-left" color="#fff" size="20" @click="back()"></van-icon>
      </template>
    </van-nav-bar>

    <div class="records">
      <van-list
        v-model="loading"
        :finished="finished"
        :finished-text="list.length ? $t('common.nomore') : ''"
        :error.sync="error"
        :error-text="$t('common.error')"
        @load="onLoad"
      >
        <div class="record-card" v-for="item in list" :key="item.order_no">
          <div class="row">
            <div class="money">{{ cleanAmount(item.money) }}</div>
            <div class="status" :class="statusClass(item.status)">{{ item.status_text }}</div>
          </div>

          <div class="meta">
            <div class="meta-line">
              <span class="meta-label">{{ $t('recharge.order_no') }}</span>{{ item.order_no }}
            </div>
            <div class="meta-line">
              <span class="meta-label">{{ $t('recharge.create_time') }}</span>{{ item.create_time }}
            </div>
            <div class="meta-line" v-if="item.pay_time">
              <span class="meta-label">{{ $t('recharge.pay_time') }}</span>{{ item.pay_time }}
            </div>
          </div>

          <div class="actions" v-if="Number(item.status) === 0 && canContinue(item)">
            <van-button
              size="small"
              round
              type="info"
              plain
              @click="continuePay(item)"
            >{{ $t('recharge.continue_pay') }}</van-button>
          </div>
        </div>

        <div v-if="!loading && !list.length" class="empty">{{ $t('recharge.no_records') }}</div>
      </van-list>
    </div>
  </div>
</template>

<script>
import { getCachedPayUrl, setPendingOrder, stripCurrency } from '@/common/recharge'
import { onMessage } from '@/common/ws'

export default {
  data() {
    return {
      list: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      finished: false,
      error: false,
      offWs: null
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.replace({ path: '/Login' })
      return
    }
    this.offWs = onMessage(this.handleWsMessage)
  },
  beforeDestroy() {
    if (this.offWs) {
      this.offWs()
      this.offWs = null
    }
  },
  methods: {
    handleWsMessage(msg) {
      if (msg && msg.type === 'recharge' && msg.event === 'paid') {
        this.handleRechargePaid(msg.data || {})
      }
    },
    handleRechargePaid(data) {
      const orderNo = data && data.order_no
      if (!orderNo) return
      const item = this.list.find(row => row.order_no === orderNo)
      if (!item) {
        // 当前列表没有该订单时，重新拉第一页
        this.refreshList()
        return
      }
      item.status = 1
      item.status_text = this.$t('recharge.status_paid')
      if (data.pay_time) item.pay_time = data.pay_time
      if (data.money) item.money = data.money
    },
    refreshList() {
      this.list = []
      this.page = 1
      this.total = 0
      this.finished = false
      this.error = false
      this.loading = true
      this.fetchList()
    },
    back() {
      return window.history.back()
    },
    cleanAmount(value) {
      return stripCurrency(value)
    },
    onLoad() {
      if (!localStorage.getItem('token')) {
        this.finished = true
        return
      }
      this.fetchList()
    },
    fetchList() {
      const page = this.page
      this.loading = true
      this.$http({
        method: 'get',
        url: 'recharge_list',
        data: { page, size: this.size }
      }).then(res => {
        this.loading = false
        if (res && res.code === 200) {
          const data = res.data || {}
          const rows = Array.isArray(data.list) ? data.list : []
          this.total = Number(data.total) || 0
          this.list = page === 1 ? rows : this.list.concat(rows)
          this.page = page + 1
          this.error = false
          this.finished =
            rows.length < this.size ||
            (this.total > 0 && this.list.length >= this.total)
          return
        }
        if (res && res.code === 401) {
          this.$toast(res.msg || '')
          try {
            localStorage.removeItem('token')
          } catch (e) {
            // ignore
          }
          this.$router.replace({ path: '/Login' })
          return
        }
        this.error = true
        this.$toast((res && res.msg) || this.$t('common.error'))
      }).catch(() => {
        this.loading = false
        this.error = true
      })
    },
    statusClass(status) {
      const value = Number(status)
      return {
        pending: value === 0,
        paid: value === 1,
        failed: value === 3
      }
    },
    canContinue(item) {
      return !!getCachedPayUrl(item.order_no)
    },
    continuePay(item) {
      const payUrl = getCachedPayUrl(item.order_no)
      if (!payUrl) {
        this.$toast(this.$t('recharge.order_expired'))
        return
      }
      setPendingOrder({ order_no: item.order_no, pay_url: payUrl })
      // 回到充值页，用内嵌 iframe 继续支付
      this.$router.push({ path: '/Recharge', query: { order_no: item.order_no } })
    }
  }
}
</script>

<style lang='less' scoped>
.container {
  min-height: 100%;
  background: linear-gradient(180deg, #fff5f8 0%, #f8f3ff 55%, #fff6f0 100%);
}

.van-nav-bar {
  background: linear-gradient(120deg, #f3a3c4 0%, #c3a6f0 55%, #ffb894 100%);
  height: 2.88rem;
  position: relative !important;

  /deep/ .van-nav-bar__title {
    color: #fff;
    font-size: 1rem;
  }
  /deep/ .van-nav-bar__left {
    padding: 0 1rem;
  }
}

.records {
  padding: 0.75rem 5% 2rem;
}

.record-card {
  margin-bottom: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: #ccc 0 0 0.22rem;

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .money {
    color: #442b89;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .status {
    padding: 0.2rem 0.65rem;
    border-radius: 0.8rem;
    font-size: 0.75rem;

    &.pending {
      color: #d48806;
      background: #fff7e6;
    }
    &.paid {
      color: #389e0d;
      background: #f6ffed;
    }
    &.failed {
      color: #cf1322;
      background: #fff1f0;
    }
  }

  .meta {
    margin-top: 0.6rem;
  }

  .meta-line {
    color: #969799;
    font-size: 0.72rem;
    line-height: 1.65;
    word-break: break-all;
  }

  .meta-label {
    margin-right: 0.35rem;
    color: #b0a7c0;
  }

  .actions {
    margin-top: 0.65rem;
    display: flex;
    justify-content: flex-end;

    /deep/ .van-button {
      height: 1.8rem;
      margin-left: 0.5rem;
      padding: 0 0.8rem;
      font-size: 0.75rem;
    }
  }
}

.empty {
  padding: 3rem 0;
  text-align: center;
  color: #969799;
  font-size: 0.85rem;
}

/deep/ .van-list__finished-text,
/deep/ .van-list__error-text {
  color: #969799;
  font-size: 0.75rem;
}
</style>
